import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Upload, ShieldCheck, X, Image as ImageIcon, Film, Music, Eye,
  Cloud, CloudOff, MonitorUp, ArrowLeftRight, FolderPlus, Trash2, Check,
  ChevronLeft, ChevronRight, Download, Link2, Share2, Play, Folder, Pencil, Sparkles, Loader2, Wand2,
} from "lucide-react";
import { useSession } from "@/context/SessionContext";
import "./gallery.css";
import {
  fetchManifest, uploadMedia, deleteMedia, getPrefs, setPrefs, downloadWithName, TRACK_FOR_EXT, EXT_FOR_FILE,
  fetchAlbums, createAlbum, deleteAlbum, transferMedia, resolveSrc, publicUrl, renameMedia, autoSortImages, cleanupLibrary, importManifest,
} from "./galleryApi";

const TABS = [
  { id: "IMAGES", label: "Graphics", icon: ImageIcon },
  { id: "VIDEOS", label: "Cinematics", icon: Film },
  { id: "AUDIO", label: "Audio Tracks", icon: Music },
];
const TRACK_META = {
  IMAGES: { label: "Image", icon: ImageIcon },
  VIDEOS: { label: "Video", icon: Film },
  AUDIO: { label: "Audio", icon: Music },
};

export default function GalleryVault() {
  const { user } = useSession();
  const isFounder = user?.role === "founder";

  const [tab, setTab] = useState("IMAGES");
  const [manifest, setManifest] = useState({ IMAGES: [], VIDEOS: [], AUDIO: [] });
  const [scope, setScope] = useState("public");
  const [autosave, setAutosave] = useState(false);
  const [dl, setDl] = useState(null); // download-name modal for gallery cards
  const [ren, setRen] = useState(null); // rename modal { item, name }
  const fileRef = useRef(null);

  // ── Ingest zone state ──────────────────────────────────────────────
  const [srcMode, setSrcMode] = useState("device"); // "device" | "vault"
  const [albums, setAlbums] = useState([]);
  const [staged, setStaged] = useState(null); // { file, name, track, ext, album }
  const [batch, setBatch] = useState(null); // multi-image: { items:[{file,name,ext}], album }
  const [sorting, setSorting] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const runAutoSort = async () => {
    if (sorting) return;
    setSorting(true);
    try {
      const r = await autoSortImages();
      toast.success(r.sorted ? `AI sorted ${r.sorted} image${r.sorted !== 1 ? "s" : ""} into albums` : "Uploads inbox is empty — nothing to sort");
      await load();
    } catch (e) { toast.error(e.message || "Auto-sort failed"); }
    finally { setSorting(false); }
  };

  const runCleanup = async () => {
    if (cleaning) return;
    setCleaning(true);
    const t = toast.loading("Cleaning library — checking for duplicates & broken images…");
    try {
      const r = await cleanupLibrary();
      const parts = [];
      if (r.deduped) parts.push(`${r.deduped} duplicate${r.deduped !== 1 ? "s" : ""} removed`);
      if (r.renamed) parts.push(`${r.renamed} name${r.renamed !== 1 ? "s" : ""} sequenced`);
      if (r.broken) parts.push(`${r.broken} broken removed`);
      if (r.resized) parts.push(`${r.resized} resized`);
      toast.success(parts.length ? `Library cleaned — ${parts.join(", ")} ✓` : "Library is already clean ✓", { id: t });
      await load();
    } catch (e) { toast.error(e.message || "Cleanup failed", { id: t }); }
    finally { setCleaning(false); }
  };
  const [vaultItems, setVaultItems] = useState({ IMAGES: [], VIDEOS: [], AUDIO: [] });
  const [picked, setPicked] = useState(null); // { item, name, album }
  const [albumModal, setAlbumModal] = useState(null); // { name, track }
  const [busy, setBusy] = useState(false);

  // Viewer navigation: album list → album grid → fullscreen · long-press menu
  const [openAlbum, setOpenAlbum] = useState(null);
  const [viewer, setViewer] = useState(null); // media item shown fullscreen
  const [menu, setMenu] = useState(null);     // { item } long-press sheet
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const enterSelect = (id) => { setSelectMode(true); setSelected(new Set([id])); };
  const exitSelect = () => { setSelectMode(false); setSelected(new Set()); };
  const [importOpen, setImportOpen] = useState(null); // paste-import modal text
  const [porting, setPorting] = useState(false);
  const buildManifestItems = () => {
    const out = [];
    ["IMAGES", "VIDEOS", "AUDIO"].forEach((tk) => (manifest[tk] || []).forEach((it) =>
      out.push({ key: it.key, track: tk, url: publicUrl(it.path), category: it.category || "", destination: it.destination || "public" })));
    return out;
  };
  const copyLibrary = async () => {
    const items = buildManifestItems();
    if (!items.length) { toast.error("Nothing to export in this view"); return; }
    const json = JSON.stringify({ misteck_library: items });
    try { await navigator.clipboard.writeText(json); } catch { /* clipboard may be blocked */ }
    try {
      const blob = new Blob([json], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = "misteck-library.json";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    } catch { /* noop */ }
    toast.success(`Exported ${items.length} items → saved misteck-library.json (also copied). Import it in preview.`);
  };
  const doImport = async () => {
    let items;
    try { const p = JSON.parse(importOpen || "{}"); items = Array.isArray(p) ? p : (p.misteck_library || p.items || []); }
    catch { toast.error("That isn't a valid library paste"); return; }
    if (!items.length) { toast.error("No items found in that paste"); return; }
    setPorting(true);
    try {
      const CHUNK = 4; let imp = 0, sk = 0, fl = 0;
      const t = toast.loading(`Importing 0/${items.length}…`);
      for (let i = 0; i < items.length; i += CHUNK) {
        const chunk = items.slice(i, i + CHUNK);
        try { const r = await importManifest(chunk); imp += r.imported || 0; sk += r.skipped || 0; fl += r.failed || 0; }
        catch { fl += chunk.length; }
        toast.loading(`Importing ${Math.min(i + CHUNK, items.length)}/${items.length}…`, { id: t });
      }
      toast.success(`Imported ${imp} · skipped ${sk}${fl ? ` · failed ${fl}` : ""}`, { id: t });
      setImportOpen(null); load();
    } finally { setPorting(false); }
  };

  const load = useCallback(async () => {
    try {
      const m = await fetchManifest(isFounder && scope === "all" ? "all" : "public");
      setManifest(m);
    } catch { toast.error("Could not load the gallery"); }
  }, [isFounder, scope]);
  useEffect(() => { load(); }, [load]);

  const loadAlbums = useCallback(async () => {
    try { setAlbums(await fetchAlbums()); } catch { /* silent */ }
  }, []);
  useEffect(() => { if (isFounder) loadAlbums(); }, [isFounder, loadAlbums]);

  useEffect(() => {
    if (!user) return;
    getPrefs().then((p) => setAutosave(!!p.autosave)).catch(() => {});
  }, [user]);

  const toggleAutosave = async () => {
    const next = !autosave;
    setAutosave(next);
    try { await setPrefs({ autosave: next }); toast.success(next ? "Cloud auto-save ON" : "Cloud auto-save OFF"); }
    catch { toast.error("Could not update setting"); setAutosave(!next); }
  };

  const albumsFor = (track) => albums.filter((a) => a.track === track);

  // ── Source: Upload from Device ─────────────────────────────────────
  const stageFile = (file) => {
    if (!file) return;
    const track = TRACK_FOR_EXT(file);
    if (!track) { toast.error(`Unsupported format "${file.type || file.name}" — use JPG/PNG/WEBP, MP4/MOV/WEBM or MP3/WAV/M4A`); return; }
    const ext = EXT_FOR_FILE(file) || TRACK_META[track].label.toLowerCase();
    const first = albumsFor(track)[0];
    setStaged({
      file, track, ext,
      name: (file.name || "clip").replace(/\.[^.]+$/, "").toLowerCase().replace(/[^a-z0-9]+/g, "_") || "clip",
      album: first ? first.name : "",
    });
  };

  const commitDevice = async () => {
    if (busy || !staged?.name.trim() || !staged.album) return;
    setBusy(true);
    try {
      await uploadMedia({ file: staged.file, key: staged.name.trim(), category: staged.album, track: staged.track, destination: "public", ext: staged.ext });
      toast.success(`Added to Stock Library · ${staged.album}`);
      setStaged(null);
      await load();
    } catch (e) { toast.error(e.message || "Upload failed"); }
    finally { setBusy(false); }
  };

  // ── Source: multi-image batch (up to 10) ──────────────────────────
  const IMG_CAP = 10;
  const genName = (file, i) =>
    (file?.name || `image_${i + 1}`).replace(/\.[^.]+$/, "").toLowerCase().replace(/[^a-z0-9]+/g, "_") || `image_${i + 1}`;

  const stageFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (files.length === 1) { stageFile(files[0]); return; }   // single → existing flow (any type)
    // Multi-select is images-only per founder request.
    const imgs = files.filter((f) => TRACK_FOR_EXT(f) === "IMAGES");
    if (!imgs.length) { toast.error("Multi-select is for images — pick JPG/PNG/WEBP files"); return; }
    const skipped = files.length - imgs.length;
    if (skipped) toast.message(`${skipped} non-image file(s) skipped — batch upload is images-only`);
    if (imgs.length > IMG_CAP) toast.message(`Max ${IMG_CAP} images per batch — extra files trimmed`);
    const capped = imgs.slice(0, IMG_CAP);
    const imgAlbums = albumsFor("IMAGES");
    const inbox = imgAlbums.find((a) => a.name === "Uploads") || imgAlbums[0];
    setStaged(null);
    setBatch({
      album: inbox ? inbox.name : "",
      items: capped.map((file, i) => ({ file, ext: EXT_FOR_FILE(file) || "png", name: genName(file, i), preview: URL.createObjectURL(file) })),
    });
  };

  const setBatchName = (i, v) => setBatch((b) => ({ ...b, items: b.items.map((it, x) => (x === i ? { ...it, name: v } : it)) }));
  const removeBatch = (i) => setBatch((b) => {
    const gone = b.items[i]; if (gone?.preview) URL.revokeObjectURL(gone.preview);
    const items = b.items.filter((_, x) => x !== i);
    return items.length ? { ...b, items } : null;
  });

  const commitBatch = async () => {
    if (busy || !batch?.items?.length || !batch.album) return;
    setBusy(true);
    let ok = 0, fail = 0;
    for (let i = 0; i < batch.items.length; i++) {
      const it = batch.items[i];
      const key = (it.name || "").trim() || `image_${i + 1}`;   // generic names allowed
      try {
        await uploadMedia({ file: it.file, key, category: batch.album, track: "IMAGES", destination: "public", ext: it.ext });
        ok++;
      } catch { fail++; }
    }
    if (fail) toast.error(`Uploaded ${ok}/${batch.items.length} to ${batch.album} · ${fail} failed`);
    else toast.success(`Added ${ok} images to Stock Library · ${batch.album}`);
    batch.items.forEach((it) => it.preview && URL.revokeObjectURL(it.preview));
    setBatch(null);
    await load();
    setBusy(false);
  };

  // ── Source: Transfer from Personal Library Vault ───────────────────
  const enterVaultMode = async () => {
    setSrcMode("vault");
    setStaged(null); setPicked(null);
    try { setVaultItems(await fetchManifest("vault")); }
    catch { toast.error("Could not load your Personal Vault"); }
  };

  const pickVault = (item, track) => {
    const first = albumsFor(track)[0];
    setPicked({ item: { ...item, track }, name: item.key || "asset", album: first ? first.name : "" });
  };

  const commitTransfer = async () => {
    if (busy || !picked?.name.trim() || !picked.album) return;
    setBusy(true);
    try {
      await transferMedia({ media_id: picked.item.id, key: picked.name.trim(), category: picked.album });
      toast.success(`Transferred to Stock Library · ${picked.album}`);
      setPicked(null);
      await load();
    } catch (e) { toast.error(e.message || "Transfer failed"); }
    finally { setBusy(false); }
  };

  // ── Create Album ───────────────────────────────────────────────────
  const submitAlbum = async () => {
    const name = (albumModal?.name || "").trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      const created = await createAlbum({ name, track: albumModal.track });
      toast.success(`Album created · ${created.name}`);
      await loadAlbums();
      // auto-select the new album in whichever staging window is open
      if (staged && staged.track === created.track) setStaged({ ...staged, album: created.name });
      if (picked && picked.item.track === created.track) setPicked({ ...picked, album: created.name });
      setAlbumModal(null);
    } catch (e) { toast.error(e.message || "Could not create album"); }
    finally { setBusy(false); }
  };

  const removeAlbum = async (a) => {
    try { await deleteAlbum(a.id); toast.success(`Removed album · ${a.name}`); await loadAlbums(); }
    catch { toast.error("Could not remove album"); }
  };

  // ── Gallery card actions ───────────────────────────────────────────
  const doDownload = (item) => setDl({ item, name: item.key || item.original_filename || "asset" });
  const confirmDownload = async () => {
    if (!dl?.name.trim()) return;
    try { await downloadWithName(dl.item, dl.name.trim()); toast.success("Saved to device"); }
    catch { toast.error("Download failed"); }
    setDl(null);
  };
  const onDelete = async (item) => {
    try { await deleteMedia(item.id); toast.success("Asset removed"); await load(); }
    catch { toast.error("Remove failed"); }
  };
  const confirmRename = async () => {
    const name = (ren?.name || "").trim();
    if (!name) return;
    try { await renameMedia(ren.item.id, name); toast.success("Renamed"); setRen(null); await load(); }
    catch (e) { toast.error(e.message || "Rename failed"); }
  };

  const items = manifest[tab] || [];

  // Build the album list for the active track: union of defined albums + any
  // categories present in the media, each with its item bucket.
  const albumBuckets = (() => {
    const names = Array.from(new Set([
      ...albumsFor(tab).map((a) => a.name),
      ...items.map((i) => i.category || "Uncategorised"),
    ]));
    return names.map((name) => ({
      name,
      items: items.filter((i) => (i.category || "Uncategorised") === name),
    })).sort((a, b) => b.items.length - a.items.length);
  })();
  const activeBucket = openAlbum ? albumBuckets.find((b) => b.name === openAlbum) : null;
  const activeAlbumView = !!activeBucket;
  const bulkDownload = async () => {
    const list = (activeBucket?.items || []).filter((it) => selected.has(it.id));
    if (!list.length) return;
    const t = toast.loading(`Downloading ${list.length}…`);
    let ok = 0;
    for (const it of list) {
      try { await downloadWithName(it, it.key || it.id); ok++; await new Promise((r) => setTimeout(r, 400)); } catch { /* skip */ }
    }
    toast.success(`Downloaded ${ok}/${list.length} to device`, { id: t });
    exitSelect();
  };
  const bulkDelete = async () => {
    const list = (activeBucket?.items || []).filter((it) => selected.has(it.id));
    if (!list.length) return;
    if (!window.confirm(`Delete ${list.length} item(s) from the Stock Library? This cannot be undone.`)) return;
    const t = toast.loading(`Deleting ${list.length}…`);
    let ok = 0;
    for (const it of list) { try { await deleteMedia(it.id); ok++; } catch { /* skip */ } }
    toast.success(`Deleted ${ok}/${list.length}`, { id: t });
    exitSelect(); load();
  };
  const shieldedItem = (it) => tab === "AUDIO" && (it.local_only || !isFounder);

  const switchTab = (id) => { setTab(id); setOpenAlbum(null); };
  const shareItem = async (it) => {
    if (shieldedItem(it)) { toast.warning("Sharing disabled (copyright shield)"); return; }
    const url = publicUrl(it.path);
    if (navigator.share) { try { await navigator.share({ title: it.key, url }); return; } catch { /* fall through */ } }
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Could not copy link"); }
    setMenu(null);
  };

  const AlbumPicker = ({ track, value, onChange }) => {
    const opts = albumsFor(track);
    return (
      <div className="mv-album-row">
        <label className="mv-album-label">
          Album <span className="req">*</span>
          <select value={value} onChange={(e) => onChange(e.target.value)} data-testid="staging-album" disabled={!opts.length}>
            {!opts.length && <option value="">No {TRACK_META[track].label} albums yet</option>}
            {opts.map((a) => <option key={a.id} value={a.name}>{a.name}</option>)}
          </select>
        </label>
        <button type="button" className="mv-newalbum" onClick={() => setAlbumModal({ name: "", track })} data-testid="open-create-album">
          <FolderPlus size={13} /> New Album
        </button>
      </div>
    );
  };

  return (
    <div className="mv-root" data-testid="gallery-root">
      <div className="mv-bg" />
      <header className="mv-header">
        <div className="mv-brand">
          <span className="mv-eyebrow">Misteck Matrix Vault</span>
          <h1>Stock Library</h1>
        </div>
        <div className="mv-header-right">
          {user && (
            <button className={`mv-autosave ${autosave ? "on" : ""}`} onClick={toggleAutosave} data-testid="gallery-autosave-toggle" title="Cloud auto-save for your picks">
              {autosave ? <Cloud size={13} /> : <CloudOff size={13} />} Auto-save {autosave ? "ON" : "OFF"}
            </button>
          )}
          {isFounder ? (
            <>
              <button className={`mv-scope ${scope === "all" ? "on" : ""}`} onClick={() => setScope((s) => (s === "all" ? "public" : "all"))} data-testid="founder-scope-toggle">
                {scope === "all" ? "All + Vault" : "Public"}
              </button>
              <span className="mv-founder-badge" data-testid="founder-badge"><ShieldCheck size={13} /> Founder</span>
            </>
          ) : (
            <span className="mv-picker-badge" data-testid="picker-mode-badge"><Eye size={13} /> Picker Mode · Read-Only</span>
          )}
        </div>
      </header>

      {/* Founder ingest zone — JWT gated */}
      {isFounder && (
        <section className="mv-founder-module" data-testid="founder-module">
          <div className="mv-src-toggle" data-testid="ingest-source-toggle">
            <button className={`mv-src ${srcMode === "device" ? "on" : ""}`} onClick={() => { setSrcMode("device"); setPicked(null); }} data-testid="src-device">
              <MonitorUp size={14} /> Upload from Device
            </button>
            <button className={`mv-src ${srcMode === "vault" ? "on" : ""}`} onClick={enterVaultMode} data-testid="src-vault">
              <ArrowLeftRight size={14} /> Transfer from Personal Library Vault
            </button>
          </div>

          {/* Cross-environment library sync — copy in prod, import/paste in preview */}
          <div style={{ display: "flex", gap: 8, padding: "0 0 8px" }}>
            <button onClick={copyLibrary} data-testid="copy-library"
              style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px", border: "2px solid #000", background: "#FFD028", color: "#000", fontFamily: "monospace", fontWeight: 800, fontSize: 11, textTransform: "uppercase", boxShadow: "2px 2px 0 #000" }}>
              <Link2 size={14} /> Export Full Library
            </button>
            <button onClick={() => setImportOpen("")} data-testid="open-import"
              style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px", border: "2px solid #000", background: "#00F5D4", color: "#000", fontFamily: "monospace", fontWeight: 800, fontSize: 11, textTransform: "uppercase", boxShadow: "2px 2px 0 #000" }}>
              <Download size={14} /> Import / Paste
            </button>
          </div>

          {/* ── Upload from Device ── */}
          {srcMode === "device" && (
            batch ? (
              <div className="mv-staging" data-testid="batch-window">
                <div className="mv-staging-head">
                  <TypeChip track="IMAGES" />
                  <span className="mv-staging-name">{batch.items.length} image{batch.items.length !== 1 ? "s" : ""} ready</span>
                  <button onClick={() => { batch.items.forEach((it) => it.preview && URL.revokeObjectURL(it.preview)); setBatch(null); }} data-testid="batch-discard-x"><X size={15} /></button>
                </div>
                <AlbumPicker track="IMAGES" value={batch.album} onChange={(v) => setBatch({ ...batch, album: v })} />
                {!batch.album && <p className="mv-interlock" data-testid="batch-interlock">Choose or create an image album to unlock the upload.</p>}
                <div data-testid="batch-list" style={{ maxHeight: 240, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6, margin: "8px 0" }}>
                  {batch.items.map((it, i) => (
                    <div key={i} data-testid={`batch-row-${i}`} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <img src={it.preview} alt="" data-testid={`batch-thumb-${i}`} style={{ width: 34, height: 34, flexShrink: 0, objectFit: "cover", border: "2px solid #000", borderRadius: 4, background: "#000" }} />
                      <input value={it.name} onChange={(e) => setBatchName(i, e.target.value)} placeholder={`image_${i + 1}`} data-testid={`batch-name-${i}`}
                        style={{ flex: 1, minWidth: 0, background: "#141414", color: "#fff", border: "2px solid #000", padding: "6px 8px", fontFamily: "monospace", fontSize: 12, outline: "none" }} />
                      <span style={{ fontFamily: "monospace", fontSize: 9, opacity: 0.4, textTransform: "uppercase" }}>.{it.ext}</span>
                      <button onClick={() => removeBatch(i)} data-testid={`batch-remove-${i}`} style={{ flexShrink: 0, opacity: 0.6 }} aria-label="Remove"><X size={13} /></button>
                    </div>
                  ))}
                </div>
                <div className="mv-staging-actions">
                  <button className="mv-commit public" onClick={commitBatch} disabled={busy || !batch.album} data-testid="batch-upload">
                    <Upload size={14} /> {busy ? "Uploading…" : `Add ${batch.items.length} Image${batch.items.length !== 1 ? "s" : ""} to Stock Library`}
                  </button>
                </div>
              </div>
            ) : !staged ? (
              <>
              <div className="mv-dropzone" data-testid="founder-dropzone"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); stageFiles(e.dataTransfer.files); }}
                onClick={() => fileRef.current?.click()}>
                <Upload size={22} />
                <span>Drop files — or tap to browse. Pick up to 10 images at once for a batch upload.</span>
                <input ref={fileRef} type="file" hidden multiple accept=".png,.jpg,.jpeg,.webp,.mp4,.webm,.mov,.m4v,.mp3,.wav,.m4a,.aac,image/*,video/*,audio/*"
                  onChange={(e) => stageFiles(e.target.files)} data-testid="founder-file-input" />
              </div>
              <button onClick={runAutoSort} disabled={sorting} data-testid="gallery-auto-sort"
                style={{ marginTop: 10, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "11px", border: "2px solid #000", background: "#B69CFF", color: "#000", fontFamily: "monospace", fontWeight: 800, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", boxShadow: "3px 3px 0px #000", cursor: "pointer" }}>
                <Sparkles size={15} /> {sorting ? "AI sorting…" : "Auto-Sort Uploads with AI"}
              </button>
              <button onClick={runCleanup} disabled={cleaning} data-testid="gallery-cleanup"
                style={{ marginTop: 8, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "11px", border: "2px solid #000", background: "#00F5D4", color: "#000", fontFamily: "monospace", fontWeight: 800, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", boxShadow: "3px 3px 0px #000", cursor: "pointer" }}>
                {cleaning ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} {cleaning ? "Cleaning…" : "Clean Library (dupes · names · broken)"}
              </button>
              </>
            ) : (
              <div className="mv-staging" data-testid="staging-window">
                <div className="mv-staging-head">
                  <TypeChip track={staged.track} />
                  <span className="mv-staging-name">{staged.file.name}</span>
                  <button onClick={() => setStaged(null)} data-testid="staging-discard-x"><X size={15} /></button>
                </div>
                <label className="mv-field">Asset Name <span className="req">*</span>
                  <input value={staged.name} onChange={(e) => setStaged({ ...staged, name: e.target.value })} placeholder="required" data-testid="staging-name" />
                </label>
                <AlbumPicker track={staged.track} value={staged.album} onChange={(v) => setStaged({ ...staged, album: v })} />
                {!staged.album && <p className="mv-interlock" data-testid="staging-interlock">Create or choose a {TRACK_META[staged.track].label} album to unlock the upload.</p>}
                <div className="mv-staging-actions">
                  <button className="mv-commit public" onClick={commitDevice} disabled={busy || !staged.name.trim() || !staged.album} data-testid="pathway-upload">
                    <Upload size={14} /> Add to Stock Library
                  </button>
                </div>
              </div>
            )
          )}

          {/* ── Transfer from Personal Library Vault ── */}
          {srcMode === "vault" && (
            picked ? (
              <div className="mv-staging" data-testid="transfer-window">
                <div className="mv-staging-head">
                  <TypeChip track={picked.item.track} />
                  <span className="mv-staging-name">{picked.item.key}</span>
                  <button onClick={() => setPicked(null)} data-testid="transfer-back"><X size={15} /></button>
                </div>
                <label className="mv-field">Asset Name <span className="req">*</span>
                  <input value={picked.name} onChange={(e) => setPicked({ ...picked, name: e.target.value })} data-testid="transfer-name" />
                </label>
                <AlbumPicker track={picked.item.track} value={picked.album} onChange={(v) => setPicked({ ...picked, album: v })} />
                {!picked.album && <p className="mv-interlock" data-testid="transfer-interlock">Create or choose a {TRACK_META[picked.item.track].label} album to unlock the transfer.</p>}
                <div className="mv-staging-actions">
                  <button className="mv-commit public" onClick={commitTransfer} disabled={busy || !picked.name.trim() || !picked.album} data-testid="pathway-transfer">
                    <ArrowLeftRight size={14} /> Transfer to Stock Library
                  </button>
                </div>
              </div>
            ) : (
              <div className="mv-vaultpick" data-testid="vault-picker">
                {["IMAGES", "VIDEOS", "AUDIO"].every((t) => (vaultItems[t] || []).length === 0) ? (
                  <p className="mv-empty">Your Personal Vault is empty — nothing to transfer.</p>
                ) : (
                  ["IMAGES", "VIDEOS", "AUDIO"].map((t) => (
                    (vaultItems[t] || []).length > 0 && (
                      <div key={t} className="mv-vaultgroup">
                        <span className="mv-vaultgroup-t"><TypeChip track={t} /> {TRACK_META[t].label}s</span>
                        <div className="mv-vaultgrid">
                          {vaultItems[t].map((it) => (
                            <button key={it.id} className="mv-vaultitem" onClick={() => pickVault(it, t)} data-testid={`vault-item-${it.id}`}>
                              <span className="mv-vaultitem-name">{it.key}</span>
                              <span className="mv-vaultitem-fmt">.{it.format}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )
                  ))
                )}
              </div>
            )
          )}
        </section>
      )}

      <nav className="mv-tabs" data-testid="gallery-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} className={`mv-tab ${tab === t.id ? "on" : ""}`} onClick={() => switchTab(t.id)}
              data-testid={`gallery-tab-${t.label.toLowerCase().replace(/\s/g, "-")}`}>
              <Icon size={15} /> {t.label}
              <span className="mv-tab-n">{(manifest[t.id] || []).length}</span>
            </button>
          );
        })}
      </nav>

      {/* Level 1 — slim vertical album list */}
      {!activeAlbumView && (
        <main className="mv-albumlist" data-testid="album-list">
          {isFounder && (
            <button className="mv-albumtile create" onClick={() => setAlbumModal({ name: "", track: tab })} data-testid="albumbar-create">
              <span className="mv-tile-cover new"><FolderPlus size={20} /></span>
              <span className="mv-tile-body"><span className="mv-tile-name">New {TRACK_META[tab].label} Album</span><span className="mv-tile-sub">Create a folder</span></span>
            </button>
          )}
          {albumBuckets.length === 0 ? (
            <p className="mv-empty" data-testid="gallery-empty">No albums in this track yet.</p>
          ) : albumBuckets.map((b) => {
            const cover = b.items[0];
            const founderAlbum = albumsFor(tab).find((a) => a.name === b.name);
            return (
              <button key={b.name} className="mv-albumtile" onClick={() => setOpenAlbum(b.name)} data-testid={`album-tile-${b.name}`}>
                <span className="mv-tile-cover">
                  {cover && tab === "IMAGES" ? <img src={resolveSrc(cover.path)} alt={b.name} loading="lazy" />
                    : tab === "VIDEOS" ? <Film size={20} /> : tab === "AUDIO" ? <Music size={20} /> : <Folder size={20} />}
                </span>
                <span className="mv-tile-body">
                  <span className="mv-tile-name">{b.name}</span>
                  <span className="mv-tile-sub">{b.items.length} item{b.items.length === 1 ? "" : "s"}</span>
                </span>
                {isFounder && founderAlbum && (
                  <span className="mv-tile-del" onClick={(e) => { e.stopPropagation(); removeAlbum(founderAlbum); }} data-testid={`album-del-${founderAlbum.id}`}><Trash2 size={14} /></span>
                )}
                <ChevronRight size={16} className="mv-tile-chev" />
              </button>
            );
          })}
        </main>
      )}

      {/* Level 2 — 4-column thumbnail grid inside an album */}
      {activeAlbumView && (
        <main className="mv-thumbs-wrap" data-testid="album-grid">
          <div className="mv-album-head">
            <button className="mv-album-back" onClick={() => { setOpenAlbum(null); exitSelect(); }} data-testid="album-back"><ChevronLeft size={16} /> Albums</button>
            <span className="mv-album-title">{openAlbum} · {activeBucket?.items.length || 0}</span>
            {(activeBucket?.items || []).length > 0 && (
              selectMode
                ? <button className="mv-album-back" style={{ marginLeft: "auto" }} onClick={exitSelect} data-testid="select-cancel"><X size={14} /> Cancel</button>
                : <button className="mv-album-back" style={{ marginLeft: "auto" }} onClick={() => setSelectMode(true)} data-testid="select-mode"><Check size={14} /> Select</button>
            )}
          </div>
          {selectMode && (
            <div data-testid="select-bar" style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 4px" }}>
              <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 800 }}>{selected.size} selected</span>
              <button onClick={() => setSelected(new Set((activeBucket?.items || []).map((i) => i.id)))} data-testid="select-all" style={{ fontFamily: "monospace", fontSize: 11, textDecoration: "underline" }}>Select all</button>
              <button onClick={bulkDownload} disabled={!selected.size} data-testid="bulk-download"
                style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", border: "2px solid #000", background: selected.size ? "#FFD028" : "#555", color: "#000", fontFamily: "monospace", fontWeight: 800, fontSize: 12, textTransform: "uppercase", boxShadow: "3px 3px 0px #000" }}>
                <Download size={14} /> Download {selected.size || ""}
              </button>
              <button onClick={bulkDelete} disabled={!selected.size} data-testid="bulk-delete"
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", border: "2px solid #000", background: selected.size ? "#ff3b4e" : "#555", color: "#000", fontFamily: "monospace", fontWeight: 800, fontSize: 12, textTransform: "uppercase", boxShadow: "3px 3px 0px #000" }}>
                <Trash2 size={14} /> Delete {selected.size || ""}
              </button>
            </div>
          )}
          {(activeBucket?.items || []).length === 0 ? (
            <p className="mv-empty">This album is empty.</p>
          ) : (
            <div className="mv-thumbs" data-testid="album-thumbs">
              {activeBucket.items.map((it) => (
                <Thumb key={it.id} item={it} track={tab} selectMode={selectMode} selected={selected.has(it.id)}
                  onOpen={() => setViewer(it)} onToggle={() => toggleSel(it.id)}
                  onHold={() => (selectMode ? toggleSel(it.id) : enterSelect(it.id))} />
              ))}
            </div>
          )}
        </main>
      )}

      {/* Create Album modal */}
      {importOpen !== null && (
        <div className="mv-modal" onClick={() => !porting && setImportOpen(null)}>
          <div className="mv-modal-box" onClick={(e) => e.stopPropagation()} data-testid="import-modal">
            <span className="mv-modal-t">Import Library</span>
            <span className="mv-modal-sub">Load the exported <b>misteck-library.json</b> file, or paste the text — files re-download into this Stock Library (vault-tagged items go to your Personal Library)</span>
            <label data-testid="import-file-label" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 8, padding: "10px", border: "2px dashed #00F5D4", borderRadius: 8, color: "#00F5D4", fontFamily: "monospace", fontWeight: 800, fontSize: 11, textTransform: "uppercase", cursor: "pointer" }}>
              <Download size={14} /> Choose .json File
              <input type="file" accept=".json,application/json" data-testid="import-file" style={{ display: "none" }}
                onChange={async (e) => { const f = e.target.files?.[0]; if (f) { try { setImportOpen(await f.text()); toast.success("File loaded — tap Import"); } catch { toast.error("Couldn't read that file"); } } }} />
            </label>
            <textarea value={importOpen} onChange={(e) => setImportOpen(e.target.value)} data-testid="import-textarea"
              placeholder='…or paste {"misteck_library":[ … ]} here' rows={5}
              style={{ width: "100%", background: "#0c0c0c", color: "#eee", border: "2px solid #000", borderRadius: 8, padding: 10, fontFamily: "monospace", fontSize: 11, marginTop: 8 }} />
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button onClick={() => setImportOpen(null)} disabled={porting} style={{ flex: 1, padding: "10px", border: "2px solid #000", background: "#222", color: "#ccc", fontFamily: "monospace", fontWeight: 800, fontSize: 11, textTransform: "uppercase" }}>Cancel</button>
              <button onClick={doImport} disabled={porting || !importOpen.trim()} data-testid="import-confirm"
                style={{ flex: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px", border: "2px solid #000", background: porting ? "#555" : "#00F5D4", color: "#000", fontFamily: "monospace", fontWeight: 800, fontSize: 11, textTransform: "uppercase", boxShadow: "2px 2px 0 #000" }}>
                {porting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Import to Library
              </button>
            </div>
          </div>
        </div>
      )}

      {albumModal && (
        <div className="mv-modal" onClick={() => setAlbumModal(null)}>
          <div className="mv-modal-box" onClick={(e) => e.stopPropagation()} data-testid="create-album-modal">
            <span className="mv-modal-t">Create Album</span>
            <span className="mv-modal-sub">Albums are typed — pick what media it holds</span>
            <div className="mv-typepick" data-testid="album-type-pick">
              {["IMAGES", "VIDEOS", "AUDIO"].map((t) => {
                const Icon = TRACK_META[t].icon;
                return (
                  <button key={t} className={`mv-typebtn ${albumModal.track === t ? "on" : ""}`}
                    onClick={() => setAlbumModal({ ...albumModal, track: t })} data-testid={`album-type-${t}`}>
                    <Icon size={14} /> {TRACK_META[t].label}
                    {albumModal.track === t && <Check size={12} className="mv-typecheck" />}
                  </button>
                );
              })}
            </div>
            <input value={albumModal.name} onChange={(e) => setAlbumModal({ ...albumModal, name: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && submitAlbum()} placeholder="Album name" autoFocus data-testid="album-name-input" />
            <button onClick={submitAlbum} disabled={!albumModal.name.trim() || busy} data-testid="album-create-confirm">Create Album</button>
          </div>
        </div>
      )}

      {/* Save-to-device name modal (gallery card download) */}
      {dl && (
        <div className="mv-modal" onClick={() => setDl(null)}>
          <div className="mv-modal-box" onClick={(e) => e.stopPropagation()}>
            <span className="mv-modal-t">Save to Device</span>
            <span className="mv-modal-sub">Enter a filename before extraction</span>
            <input value={dl.name} onChange={(e) => setDl({ ...dl, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && confirmDownload()} data-testid="download-name-input" autoFocus />
            <button onClick={confirmDownload} disabled={!dl.name.trim()} data-testid="download-confirm">Download</button>
          </div>
        </div>
      )}
      {/* Rename asset modal (founder) */}
      {ren && (
        <div className="mv-modal" onClick={() => setRen(null)}>
          <div className="mv-modal-box" onClick={(e) => e.stopPropagation()} data-testid="rename-modal">
            <span className="mv-modal-t">Rename Asset</span>
            <span className="mv-modal-sub">Give this asset a clear name</span>
            <input value={ren.name} onChange={(e) => setRen({ ...ren, name: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && confirmRename()} data-testid="rename-input" autoFocus />
            <button onClick={confirmRename} disabled={!ren.name.trim()} data-testid="rename-confirm">Save Name</button>
          </div>
        </div>
      )}
      {/* Fullscreen media viewer */}
      {viewer && (
        <div className="mv-viewer" onClick={() => setViewer(null)} data-testid="media-viewer">
          <button className="mv-viewer-x" onClick={() => setViewer(null)} data-testid="viewer-close"><X size={20} /></button>
          <div className="mv-viewer-stage" onClick={(e) => e.stopPropagation()}>
            <FullMedia item={viewer} track={tab} />
          </div>
          <span className="mv-viewer-cap">{viewer.key}</span>
        </div>
      )}

      {/* Long-press action sheet — download / share / (delete) */}
      {menu && (
        <div className="mv-sheet-scrim" onClick={() => setMenu(null)} data-testid="media-menu">
          <div className="mv-sheet" onClick={(e) => e.stopPropagation()}>
            <span className="mv-sheet-t">{menu.item.key}</span>
            <button className="mv-sheet-btn" onClick={() => { doDownload(menu.item); setMenu(null); }} data-testid="menu-download">
              <Download size={16} /> Save to device
            </button>
            <button className="mv-sheet-btn" disabled={shieldedItem(menu.item)} onClick={() => shareItem(menu.item)} data-testid="menu-share">
              <Share2 size={16} /> {shieldedItem(menu.item) ? "Sharing disabled" : "Share / copy link"}
            </button>
            {isFounder && (
              <button className="mv-sheet-btn" onClick={() => { setRen({ item: menu.item, name: menu.item.key || "" }); setMenu(null); }} data-testid="menu-rename">
                <Pencil size={16} /> Rename asset
              </button>
            )}
            {isFounder && (
              <button className="mv-sheet-btn del" onClick={() => { onDelete(menu.item); setMenu(null); }} data-testid="menu-delete">
                <Trash2 size={16} /> Remove asset
              </button>
            )}
            <button className="mv-sheet-btn cancel" onClick={() => setMenu(null)} data-testid="menu-cancel">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Thumbnail — tap opens fullscreen; press-and-hold (or right-click) opens the sheet.
const Thumb = ({ item, track, selectMode, selected, onOpen, onHold, onToggle }) => {
  const timer = useRef(null);
  const held = useRef(false);
  const start = () => { held.current = false; timer.current = setTimeout(() => { held.current = true; onHold(); }, 450); };
  const clear = () => { if (timer.current) clearTimeout(timer.current); };
  const click = () => { if (held.current) return; selectMode ? onToggle() : onOpen(); };
  return (
    <button className={`mv-thumb ${selected ? "sel" : ""}`} data-testid={`media-thumb-${item.id}`}
      onClick={click}
      onContextMenu={(e) => { e.preventDefault(); onHold(); }}
      onTouchStart={start} onTouchEnd={clear} onTouchMove={clear}
      onMouseDown={start} onMouseUp={clear} onMouseLeave={clear}
      style={selected ? { outline: "3px solid #00F5D4", outlineOffset: -3 } : undefined}>
      {track === "IMAGES" && <img src={resolveSrc(item.path)} alt={item.key} loading="lazy" />}
      {track === "VIDEOS" && <><video src={resolveSrc(item.path)} muted preload="metadata" /><span className="mv-thumb-play"><Play size={16} /></span></>}
      {track === "AUDIO" && <span className="mv-thumb-audio"><Music size={18} /></span>}
      {item.destination === "vault" && <span className="mv-thumb-vault">V</span>}
      {selectMode && (
        <span data-testid={`media-tick-${item.id}`} style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%", border: "2px solid #fff", background: selected ? "#00F5D4" : "rgba(0,0,0,0.5)", display: "grid", placeItems: "center", zIndex: 2 }}>
          {selected && <Check size={12} color="#000" />}
        </span>
      )}
    </button>
  );
};

// Fullscreen media renderer.
const FullMedia = ({ item, track }) => {
  const src = resolveSrc(item.path);
  if (track === "IMAGES") return <img className="mv-full-img" src={src} alt={item.key} />;
  if (track === "VIDEOS") return <video className="mv-full-vid" src={src} controls autoPlay playsInline />;
  return <audio className="mv-full-aud" src={src} controls autoPlay />;
};

const TypeChip = ({ track }) => {
  const meta = TRACK_META[track];
  const Icon = meta.icon;
  return <span className={`mv-typechip ${track.toLowerCase()}`}><Icon size={11} /> {meta.label}</span>;
};
